import {
  Box,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import color from "../../components/utils/Colors";
import NotificationPreferences from "./NotificationPreferences";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "@mui/icons-material";
import { getUserNotifications } from "../../services/services";
import { useUser } from "../../services/Context/UserContext";

const Notification: React.FC = () => {
  const [value, setValue] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useUser();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  const isSmallScreen = useMediaQuery("(max-width:900px)");

  useEffect(() => {
    if (!user?.id || value !== 0) return;
    setLoading(true);
    getUserNotifications(user.id)
      .then((res: any) => {
        setNotifications(res?.data?.data ?? []);
      })
      .catch(() => {
        console.error("Failed to fetch notifications");
      })
      .finally(() => setLoading(false));
  }, [user?.id, value]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffHours < 1) return "Just now";
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
  };

  return (
    <Box
      sx={{
        p: 4,
        pt: isSmallScreen ? 4 : 2,
        width: isSmallScreen ? "100%" : "70%",
        margin: "auto",
        minHeight: "80vh",
      }}
    >
      <Typography
        onClick={handleGoBack}
        sx={{
          margin: 0,
          position: "absolute",
          top: isSmallScreen ? "120px" : "140px",
          color: color.firstColor,
          fontSize: "16px",
          fontWeight: "bold",
          left: isSmallScreen ? 10 : 60,
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <ChevronLeft></ChevronLeft> Back
      </Typography>
      <Tabs
        sx={{
          "& .MuiTabs-indicator": {
            backgroundColor: color.borderColor,
          },
        }}
        value={value}
        onChange={handleChange}
        centered
      >
        <Tab
          style={{
            color: color.textColor,
            textTransform: "none",
            fontWeight: "bold",
            fontSize: isSmallScreen ? "14px" : "22px",
          }}
          label="Notification"
        />
        <Tab
          style={{
            color: color.textColor,
            textTransform: "none",
            fontWeight: "bold",
            fontSize: isSmallScreen ? "14px" : "22px",
          }}
          label="Notification Preferences"
        />
      </Tabs>
      {value === 0 && (
        <Box
          sx={(theme) => ({
            marginTop: "25px",
            flex: 1,
            minHeight: "80vh",
            pt: "2%",
            borderRadius: "16px",
            bgcolor:
              theme.palette.mode === "light"
                ? "rgba(255, 255, 255, 0.4)"
                : "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(2px)",
            border: "solid 1px",
            borderColor: color.borderColor,
          })}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: color.firstColor }} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography sx={{ color: "gray", fontSize: "16px" }}>
                No notifications yet.
              </Typography>
            </Box>
          ) : (
            notifications.map((notif: any, index: number) => (
              <Box
                key={notif.id ?? index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  borderBottom: "solid 1px",
                  borderColor: color.borderColor,
                  p: 2,
                  gap: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  {notif.title && (
                    <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                      {notif.title}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: "14px" }}>
                    {notif.message}
                  </Typography>
                  {notif.type && (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: color.firstColor,
                        mt: 0.5,
                        textTransform: "capitalize",
                      }}
                    >
                      {notif.type}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{ whiteSpace: "nowrap", color: "gray", fontSize: "13px" }}
                >
                  {formatDate(notif.createdAt)}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      )}
      {value === 1 && <NotificationPreferences></NotificationPreferences>}
    </Box>
  );
};

export default Notification;
